import React, { useState, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, ClipboardList, Camera, Loader2, CheckCircle, User, Phone, Search, UserPlus, Sparkles, Mic, UserX, Building2, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ComprehensiveDiagnosis } from '../components/diagnosis';
import { VoiceRecorder } from '../components/voice';
import ExpressDiagnosisPage from './ExpressDiagnosisPage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SkinAnalysisPage = ({ onBack, onComplete }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [method, setMethod] = useState(null); // 'questionnaire' or 'camera'
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientId, setClientId] = useState(null);
  const [searchingClient, setSearchingClient] = useState(false);
  const [existingClient, setExistingClient] = useState(null);

  // ── Identification unifiée (Step 0) ─────────────────────────────────────
  const [identMode, setIdentMode] = useState('phone'); // 'phone' | 'code'
  const [clientCode, setClientCode] = useState('');
  const [clientBirthYear, setClientBirthYear] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [identConfirmed, setIdentConfirmed] = useState(false);
  const [codeLookupError, setCodeLookupError] = useState(null);
  
  const isAgent = user?.role === 'agent' || user?.role === 'admin' || user?.is_admin;
  
  // Questionnaire state
  const [answers, setAnswers] = useState({
    skin_type: '',
    has_acne: null,
    has_dark_spots: null,
    is_sensitive: null,
    age_group: ''
  });
  
  // Camera state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const questions = [
    {
      key: 'skin_type',
      question: t('skinType'),
      type: 'radio',
      options: [
        { value: 'dry', label: t('dry') },
        { value: 'oily', label: t('oily') },
        { value: 'combination', label: t('combination') },
        { value: 'normal', label: t('normal') }
      ]
    },
    {
      key: 'has_acne',
      question: t('hasAcne'),
      type: 'boolean'
    },
    {
      key: 'has_dark_spots',
      question: t('hasDarkSpots'),
      type: 'boolean'
    },
    {
      key: 'is_sensitive',
      question: t('isSensitive'),
      type: 'boolean'
    },
    {
      key: 'age_group',
      question: t('ageGroup'),
      type: 'radio',
      options: [
        { value: '<20', label: '< 20' },
        { value: '20-30', label: '20 - 30' },
        { value: '30-40', label: '30 - 40' },
        { value: '40+', label: '40+' }
      ]
    }
  ];

  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError(language === 'fr' 
        ? 'Impossible d\'accéder à la caméra. Veuillez autoriser l\'accès.'
        : 'Unable to access camera. Please allow access.');
    }
  }, [language]);

  const searchClientByPhone = async () => {
    if (!clientPhone || clientPhone.length < 8) return;
    
    setSearchingClient(true);
    try {
      const response = await axios.get(`${API_URL}/clients/search/${clientPhone}`);
      if (response.data) {
        setExistingClient(response.data);
        setClientId(response.data.id);
        setClientName(`${response.data.first_name} ${response.data.last_name}`);
        toast.success(language === 'fr' ? 'Client trouvé!' : 'Client found!');
      } else {
        setExistingClient(null);
        setClientId(null);
      }
    } catch (error) {
      setExistingClient(null);
      setClientId(null);
    } finally {
      setSearchingClient(false);
    }
  };

  const createNewClient = async () => {
    if (!clientName || !clientPhone) {
      toast.error(language === 'fr' ? 'Nom et téléphone requis' : 'Name and phone required');
      return null;
    }
    
    try {
      // Split name into first and last name
      const nameParts = clientName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const response = await axios.post(`${API_URL}/clients`, {
        first_name: firstName,
        last_name: lastName,
        phone: clientPhone,
        skin_type: answers.skin_type || null
      });
      
      setClientId(response.data.id);
      setExistingClient(response.data);
      return response.data.id;
    } catch (error) {
      if (error.response?.data?.detail === 'Ce client existe déjà') {
        // Client already exists, search for them
        await searchClientByPhone();
        return clientId;
      }
      console.error('Failed to create client:', error);
      return null;
    }
  };

  // ── Identification : génération d'un code SK-XXXX (mode "sans téléphone") ──
  const generateClientCode = async () => {
    if (!clientName || clientName.trim().length < 2) {
      toast.error(language === 'fr' ? 'Prénom requis pour générer un code' : 'First name required');
      return;
    }
    setGeneratingCode(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const nameParts = clientName.trim().split(' ');
      const payload = {
        first_name: nameParts[0],
        last_name: nameParts.slice(1).join(' ') || null,
        birth_year: clientBirthYear ? parseInt(clientBirthYear, 10) : null,
      };
      const response = await axios.post(`${API_URL}/comprehensive/client/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const code = response.data?.generated_client_code || response.data?.client?.client_code;
      if (!code) throw new Error('Code non généré');
      setGeneratedCode(code);
      setClientCode(code);
      setClientId(response.data.client?.id || response.data.id);
      toast.success(language === 'fr' ? `Code généré : ${code}` : `Code: ${code}`);
    } catch (error) {
      console.error('Code generation failed:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de génération du code' : 'Code generation error'));
    } finally {
      setGeneratingCode(false);
    }
  };

  // ── Identification : recherche d'un client existant par son code ──────────
  const searchClientByCode = async () => {
    const code = clientCode.trim().toUpperCase();
    if (!code || code.length < 5) {
      setCodeLookupError(language === 'fr' ? 'Code trop court (format SK-XXXX)' : 'Code too short');
      return;
    }
    setCodeLookupError(null);
    setSearchingClient(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const params = new URLSearchParams();
      if (clientName) params.set('first_name', clientName.trim().split(' ')[0]);
      if (clientBirthYear) params.set('birth_year', clientBirthYear);
      const url = `${API_URL}/comprehensive/client/by-code/${encodeURIComponent(code)}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const c = response.data?.client;
      if (c) {
        setExistingClient(c);
        setClientId(c.id);
        setClientName(`${c.first_name || ''} ${c.last_name || ''}`.trim());
        if (c.birth_year) setClientBirthYear(String(c.birth_year));
        toast.success(language === 'fr' ? 'Client retrouvé via son code' : 'Client found');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || (language === 'fr' ? 'Code introuvable' : 'Code not found');
      setCodeLookupError(msg);
      setExistingClient(null);
    } finally {
      setSearchingClient(false);
    }
  };

  // ── Validation : peut-on passer à l'étape "choix du mode" ? ──────────────
  const canConfirmIdent = () => {
    if (!clientName || clientName.trim().length < 2) return false;
    if (identMode === 'phone') return clientPhone && clientPhone.length >= 8;
    // identMode === 'code' : il faut soit un code généré, soit un code existant validé
    return Boolean(generatedCode) || Boolean(existingClient);
  };

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      stopCamera();
    }
  }, [stopCamera]);

  const handleMethodSelect = (selectedMethod) => {
    // L'identification doit être confirmée AVANT le choix du mode
    if (!identConfirmed) {
      toast.error(language === 'fr' 
        ? 'Veuillez d\'abord identifier le client' 
        : 'Please identify the client first');
      return;
    }
    setMethod(selectedMethod);
    if (selectedMethod === 'camera') {
      setTimeout(startCamera, 100);
    }
  };

  const handleAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    if (method === 'questionnaire') {
      const currentQuestion = questions[step];
      if (currentQuestion.type === 'boolean') {
        return answers[currentQuestion.key] !== null;
      }
      return answers[currentQuestion.key] !== '';
    }
    return true;
  };

  const submitQuestionnaire = async () => {
    setLoading(true);
    try {
      // Create client if new
      let finalClientId = clientId;
      if (isAgent && !existingClient && clientPhone && clientName) {
        finalClientId = await createNewClient();
      }
      
      const payload = {
        ...answers,
        ...(isAgent && clientName ? { client_name: clientName } : {}),
        ...(finalClientId ? { client_id: finalClientId } : {}),
        ...(clientPhone ? { client_phone: clientPhone } : {})
      };
      const response = await axios.post(`${API_URL}/diagnosis/questionnaire`, payload);
      toast.success(language === 'fr' ? 'Analyse terminée!' : 'Analysis complete!');
      
      // Pass client info to results
      const resultWithClient = {
        ...response.data,
        client_id: finalClientId,
        client_phone: clientPhone
      };
      onComplete(resultWithClient);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur d\'analyse' : 'Analysis error'));
    } finally {
      setLoading(false);
    }
  };

  const submitScan = async () => {
    if (!capturedImage) return;
    
    setLoading(true);
    try {
      // Create client if new
      let finalClientId = clientId;
      if (isAgent && !existingClient && clientPhone && clientName) {
        finalClientId = await createNewClient();
      }
      
      // Remove data URL prefix to get base64
      const base64 = capturedImage.split(',')[1];
      const payload = {
        image_base64: base64,
        ...(isAgent && clientName ? { client_name: clientName } : {}),
        ...(finalClientId ? { client_id: finalClientId } : {}),
        ...(clientPhone ? { client_phone: clientPhone } : {})
      };
      const response = await axios.post(`${API_URL}/diagnosis/scan`, payload);
      toast.success(language === 'fr' ? 'Analyse IA terminée!' : 'AI analysis complete!');
      
      // Pass client info to results
      const resultWithClient = {
        ...response.data,
        client_id: finalClientId,
        client_phone: clientPhone
      };
      onComplete(resultWithClient);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur d\'analyse' : 'Analysis error'));
    } finally {
      setLoading(false);
    }
  };

  // ── Étape 1 : Identification (Phone OU Code SK-XXXX) ────────────────────
  const renderIdentification = () => (
    <div className="space-y-5 animate-fade-in-up" data-testid="identification-step">
      <div className="text-center">
        <span className="inline-block px-3 py-1 rounded-full bg-[#E0F2FE] text-[#0C4A6E] text-xs font-bold tracking-widest uppercase">
          {language === 'fr' ? 'Étape 1 / 2 · Identification' : 'Step 1 / 2 · Identification'}
        </span>
        <h2 className="text-2xl text-[#0F172A] mt-3">
          {language === 'fr' ? 'Identifier le client' : 'Identify the client'}
        </h2>
        <p className="text-sm text-[#78716C] mt-1">
          {language === 'fr' 
            ? 'Téléphone OU code client (si le client refuse de donner son numéro)' 
            : 'Phone OR client code (if client refuses to share phone)'}
        </p>
      </div>

      <Card className="border-[#0EA5E9] bg-[#E0F2FE]/10">
        <CardContent className="p-4 space-y-4">
          {/* Toggle Phone / Code */}
          <div className="flex gap-2" data-testid="ident-mode-toggle">
            <button
              type="button"
              onClick={() => { setIdentMode('phone'); setCodeLookupError(null); setGeneratedCode(null); }}
              className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition ${
                identMode === 'phone' ? 'border-[#0EA5E9] bg-[#E0F2FE] text-[#0C4A6E]' : 'border-zinc-200 bg-white text-zinc-600'
              }`}
              data-testid="ident-mode-phone"
            >
              <Phone className="w-4 h-4 inline mr-1.5" />
              {language === 'fr' ? 'Téléphone' : 'Phone'}
            </button>
            <button
              type="button"
              onClick={() => { setIdentMode('code'); setClientPhone(''); setExistingClient(null); }}
              className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition ${
                identMode === 'code' ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-zinc-200 bg-white text-zinc-600'
              }`}
              data-testid="ident-mode-code"
            >
              <UserX className="w-4 h-4 inline mr-1.5" />
              {language === 'fr' ? 'Sans téléphone' : 'No phone'}
            </button>
          </div>

          {/* Prénom (toujours requis) */}
          <div>
            <Label className="text-sm mb-1 block">
              <User className="w-4 h-4 inline mr-1.5" />
              {language === 'fr' ? 'Prénom du client' : 'Client first name'}
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder={language === 'fr' ? 'Ex : Awa' : 'Ex: Awa'}
              className="bg-white h-11"
              data-testid="ident-name-input"
            />
          </div>

          {/* Mode TÉLÉPHONE */}
          {identMode === 'phone' && (
            <div>
              <Label className="text-sm mb-1 block">
                <Phone className="w-4 h-4 inline mr-1.5" />
                {language === 'fr' ? 'Téléphone' : 'Phone'}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  value={clientPhone}
                  onChange={(e) => {
                    setClientPhone(e.target.value);
                    setExistingClient(null);
                    setClientId(null);
                  }}
                  placeholder="07 12 34 56 78"
                  className="bg-white h-11"
                  data-testid="client-phone-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={searchClientByPhone}
                  disabled={searchingClient || clientPhone.length < 8}
                  className="h-11"
                  data-testid="search-client-btn"
                >
                  {searchingClient ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {existingClient && (
                <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm font-medium flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4" />
                    {existingClient.first_name} {existingClient.last_name}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Mode CODE (Sans téléphone) */}
          {identMode === 'code' && (
            <div className="space-y-3">
              <div>
                <Label className="text-sm mb-1 block">
                  <KeyRound className="w-4 h-4 inline mr-1.5 text-amber-700" />
                  {language === 'fr' ? 'Code client existant (si déjà venu)' : 'Existing client code'}
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={clientCode}
                    onChange={(e) => { setClientCode(e.target.value.toUpperCase()); setCodeLookupError(null); }}
                    placeholder="SK-XXXX"
                    className="bg-white font-mono uppercase h-11"
                    data-testid="ident-code-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={searchClientByCode}
                    disabled={searchingClient || !clientCode || clientCode.length < 5}
                    className="h-11"
                    data-testid="ident-code-search-btn"
                  >
                    {searchingClient ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                {codeLookupError && (
                  <p className="text-xs text-red-600 mt-1" data-testid="ident-code-error">{codeLookupError}</p>
                )}
                {existingClient && (
                  <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm font-medium flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4" />
                      {existingClient.first_name} {existingClient.last_name}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm mb-1 block">
                  {language === 'fr' ? 'Année de naissance (optionnel)' : 'Birth year (optional)'}
                </Label>
                <Input
                  type="number"
                  min="1900"
                  max="2025"
                  value={clientBirthYear}
                  onChange={(e) => setClientBirthYear(e.target.value)}
                  placeholder="1995"
                  className="bg-white h-11"
                  data-testid="ident-birth-year"
                />
              </div>

              {/* Bouton générer code pour nouveau client */}
              {!existingClient && !generatedCode && (
                <Button
                  type="button"
                  onClick={generateClientCode}
                  disabled={generatingCode || !clientName || clientName.trim().length < 2}
                  className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  data-testid="ident-generate-code-btn"
                >
                  {generatingCode ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-5 h-5 mr-2" />
                  )}
                  {language === 'fr' ? 'Générer un code pour ce nouveau client' : 'Generate code for new client'}
                </Button>
              )}

              {/* Carte photographiable avec code généré */}
              {generatedCode && (
                <div
                  className="rounded-2xl border-2 border-amber-300 overflow-hidden shadow-lg"
                  data-testid="generated-code-card"
                >
                  <div className="bg-gradient-to-r from-amber-600 to-orange-700 text-white px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      <h3 className="text-sm font-bold leading-tight">Pharmacie Saint Ange Des Amis</h3>
                    </div>
                    <p className="text-[10px] text-amber-100 mt-0.5">
                      Angré 7<sup>e</sup> Tranche · Cocody · Abidjan
                    </p>
                    <p className="text-[10px] text-amber-100">
                      Mobile : 07 78 41 52 77 · Fixe : 27 22 30 05 72
                    </p>
                  </div>
                  <div className="bg-amber-50 p-5 text-center">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-widest mb-2">
                      {language === 'fr' ? 'Code client' : 'Client code'}
                    </p>
                    <p className="text-4xl font-bold text-amber-900 font-mono tracking-wider mb-3" data-testid="generated-code-value">
                      {generatedCode}
                    </p>
                    <p className="text-[11px] text-amber-700 mt-3 flex items-center justify-center gap-1">
                      <Camera className="w-3 h-3" />
                      {language === 'fr' 
                        ? 'Photographiez ou notez ce code — il identifie le client à ses prochains passages.' 
                        : 'Photograph or write down this code.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bouton CONFIRMER → passe au choix du mode */}
      <Button
        onClick={() => setIdentConfirmed(true)}
        disabled={!canConfirmIdent()}
        className="w-full h-12 btn-primary"
        data-testid="ident-confirm-btn"
      >
        {language === 'fr' ? 'Continuer vers le choix du conseil' : 'Continue to advice mode'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  // ── Étape 2 : Choix du mode de conseil ──────────────────────────────────
  const renderMethodSelection = () => (
    <div className="space-y-6 animate-fade-in-up">
      {/* Récap client (compact, modifiable via "Modifier") */}
      <Card className="border-[#0EA5E9] bg-[#E0F2FE]/20" data-testid="client-summary-card">
        <CardContent className="p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#0EA5E9] flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#0C4A6E] truncate">{clientName}</p>
              <p className="text-xs text-[#0C4A6E]/70 truncate font-mono">
                {identMode === 'phone' ? clientPhone : (generatedCode || clientCode)}
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIdentConfirmed(false)}
            className="h-8 text-xs"
            data-testid="edit-ident-btn"
          >
            {language === 'fr' ? 'Modifier' : 'Edit'}
          </Button>
        </CardContent>
      </Card>

      <div className="text-center">
        <span className="inline-block px-3 py-1 rounded-full bg-[#E0F2FE] text-[#0C4A6E] text-xs font-bold tracking-widest uppercase">
          {language === 'fr' ? 'Étape 2 / 2 · Mode de conseil' : 'Step 2 / 2 · Advice mode'}
        </span>
        <h2 className="text-2xl text-[#0F172A] mt-3">{t('chooseMethod')}</h2>
      </div>

      <div className="grid gap-3">
        {/* MODE EXPRESS — mis en avant en premier */}
        <Card
          className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${
            method === 'express' ? 'border-amber-500 bg-amber-50' : 'border-amber-200'
          }`}
          onClick={() => handleMethodSelect('express')}
          data-testid="method-express"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-[#0F172A]">
                  {language === 'fr' ? 'Mode Express' : 'Express Mode'}
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                  ⚡ {language === 'fr' ? '4 questions' : '4 questions'}
                </span>
              </div>
              <p className="text-sm text-[#78716C]">
                {language === 'fr' 
                  ? 'Diagnostic rapide → produits recommandés (~1 min)'
                  : 'Quick diagnosis → recommended products (~1 min)'}
              </p>
            </div>
            {method === 'express' && <CheckCircle className="w-6 h-6 text-amber-600" />}
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${
            method === 'questionnaire' ? 'border-[#0EA5E9] bg-[#E0F2FE]/20' : 'border-[#E7E5E4]'
          }`}
          onClick={() => handleMethodSelect('questionnaire')}
          data-testid="method-questionnaire"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-7 h-7 text-[#0EA5E9]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">{t('questionnaire')}</h3>
              <p className="text-sm text-[#78716C]">{t('questionnaireDesc')}</p>
            </div>
            {method === 'questionnaire' && (
              <CheckCircle className="w-6 h-6 text-[#0EA5E9] ml-auto" />
            )}
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${
            method === 'camera' ? 'border-[#0EA5E9] bg-[#E0F2FE]/20' : 'border-[#E7E5E4]'
          }`}
          onClick={() => handleMethodSelect('camera')}
          data-testid="method-camera"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
              <Camera className="w-7 h-7 text-[#0EA5E9]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">{t('cameraScan')}</h3>
              <p className="text-sm text-[#78716C]">{t('cameraScanDesc')}</p>
            </div>
            {method === 'camera' && (
              <CheckCircle className="w-6 h-6 text-[#0EA5E9] ml-auto" />
            )}
          </CardContent>
        </Card>

        {/* Diagnostic complet (peau + cheveux + ongles) */}
        <Card 
          className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${
            method === 'comprehensive' ? 'border-[#8B5CF6] bg-purple-50' : 'border-[#E7E5E4]'
          }`}
          onClick={() => handleMethodSelect('comprehensive')}
          data-testid="method-comprehensive"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-7 h-7 text-[#8B5CF6]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">
                {language === 'fr' ? 'Diagnostic Complet' : 'Complete Diagnosis'}
              </h3>
              <p className="text-sm text-[#78716C]">
                {language === 'fr' 
                  ? 'Peau (visage, corps, mains) + Cheveux + Ongles • Profil client sauvegardé'
                  : 'Skin (face, body, hands) + Hair + Nails • Client profile saved'}
              </p>
              <div className="flex gap-1 mt-1">
                <span className="text-lg">🧴</span>
                <span className="text-lg">💇</span>
                <span className="text-lg">💅</span>
              </div>
            </div>
            {method === 'comprehensive' && (
              <CheckCircle className="w-6 h-6 text-[#8B5CF6] ml-auto" />
            )}
          </CardContent>
        </Card>

        {/* Diagnostic Vocal */}
        <Card 
          className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${
            method === 'voice' ? 'border-[#10B981] bg-green-50' : 'border-[#E7E5E4]'
          }`}
          onClick={() => handleMethodSelect('voice')}
          data-testid="method-voice"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <Mic className="w-7 h-7 text-[#10B981]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">
                {language === 'fr' ? 'Diagnostic Vocal' : 'Voice Diagnosis'}
              </h3>
              <p className="text-sm text-[#78716C]">
                {language === 'fr' 
                  ? 'Décrivez les symptômes à voix haute • L\'IA transcrit et analyse'
                  : 'Describe symptoms out loud • AI transcribes and analyzes'}
              </p>
              <div className="flex gap-1 mt-1">
                <span className="text-lg">🎤</span>
                <span className="text-lg">✨</span>
              </div>
            </div>
            {method === 'voice' && (
              <CheckCircle className="w-6 h-6 text-[#10B981] ml-auto" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderQuestionnaire = () => {
    const currentQuestion = questions[step];
    const progress = ((step + 1) / questions.length) * 100;

    return (
      <div className="space-y-8 animate-fade-in-up">
        <Progress value={progress} className="h-2" />
        
        <div className="text-center">
          <span className="text-sm text-[#78716C]">
            {language === 'fr' ? 'Question' : 'Question'} {step + 1}/{questions.length}
          </span>
        </div>

        <Card className="border-[#E7E5E4]">
          <CardContent className="p-6 space-y-6">
            <h3 className="text-xl text-[#0F172A] text-center">{currentQuestion.question}</h3>

            {currentQuestion.type === 'radio' && (
              <RadioGroup
                value={answers[currentQuestion.key]}
                onValueChange={(value) => handleAnswer(currentQuestion.key, value)}
                className="grid grid-cols-2 gap-3"
              >
                {currentQuestion.options.map((option) => (
                  <div key={option.value}>
                    <RadioGroupItem
                      value={option.value}
                      id={option.value}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={option.value}
                      className="flex items-center justify-center p-4 rounded-xl border-2 border-[#E7E5E4] cursor-pointer transition-all duration-200 peer-data-[state=checked]:border-[#0EA5E9] peer-data-[state=checked]:bg-[#E0F2FE]/30 hover:bg-[#F5F5F4]"
                      data-testid={`option-${option.value}`}
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            {currentQuestion.type === 'boolean' && (
              <div className="grid grid-cols-2 gap-4">
                <Button
                  type="button"
                  variant={answers[currentQuestion.key] === true ? 'default' : 'outline'}
                  className={`h-14 text-lg ${
                    answers[currentQuestion.key] === true 
                      ? 'bg-[#0EA5E9] hover:bg-[#0284C7]' 
                      : 'border-[#E7E5E4] hover:bg-[#F5F5F4]'
                  }`}
                  onClick={() => handleAnswer(currentQuestion.key, true)}
                  data-testid="option-yes"
                >
                  {t('yes')}
                </Button>
                <Button
                  type="button"
                  variant={answers[currentQuestion.key] === false ? 'default' : 'outline'}
                  className={`h-14 text-lg ${
                    answers[currentQuestion.key] === false 
                      ? 'bg-[#0EA5E9] hover:bg-[#0284C7]' 
                      : 'border-[#E7E5E4] hover:bg-[#F5F5F4]'
                  }`}
                  onClick={() => handleAnswer(currentQuestion.key, false)}
                  data-testid="option-no"
                >
                  {t('no')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => step > 0 ? setStep(step - 1) : setMethod(null)}
            className="flex-1"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('previous')}
          </Button>
          
          {step < questions.length - 1 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 btn-primary"
              data-testid="next-btn"
            >
              {t('next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={submitQuestionnaire}
              disabled={!canProceed() || loading}
              className="flex-1 btn-primary"
              data-testid="analyze-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('analyze')}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderCamera = () => (
    <div className="space-y-6 animate-fade-in-up">
      <div className="relative aspect-[3/4] max-w-sm mx-auto rounded-3xl overflow-hidden bg-[#1C1917]">
        {cameraError ? (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
            <p className="text-white">{cameraError}</p>
          </div>
        ) : capturedImage ? (
          <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {/* Face guide overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-64 h-80 border-4 border-[#0EA5E9]/60 rounded-[50%] animate-pulse-soft" />
            </div>
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {!capturedImage && !cameraError && (
        <div className="text-center space-y-2">
          <p className="text-[#0F172A] font-medium">{t('positionFace')}</p>
          <p className="text-sm text-[#78716C]">{t('goodLighting')}</p>
        </div>
      )}

      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => {
            stopCamera();
            setCapturedImage(null);
            setMethod(null);
          }}
          className="flex-1"
          data-testid="camera-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('back')}
        </Button>

        {capturedImage ? (
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCapturedImage(null);
                startCamera();
              }}
              className="flex-1"
              data-testid="retake-btn"
            >
              {t('retake')}
            </Button>
            <Button
              onClick={submitScan}
              disabled={loading}
              className="flex-1 btn-primary"
              data-testid="submit-scan-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('analyze')}
            </Button>
          </>
        ) : (
          <Button
            onClick={capturePhoto}
            disabled={!stream || cameraError}
            className="flex-1 btn-primary"
            data-testid="capture-btn"
          >
            <Camera className="w-5 h-5 mr-2" />
            {t('takePhoto')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => {
              stopCamera();
              if (method) {
                setMethod(null);
              } else if (identConfirmed) {
                setIdentConfirmed(false);
              } else {
                onBack();
              }
            }}
            className="flex items-center gap-2 text-[#78716C] hover:text-[#0F172A] transition-colors"
            data-testid="analysis-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl text-[#0F172A] mx-auto pr-7">{t('skinAnalysis')}</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-lg mx-auto px-4 py-8">
        {!method && !identConfirmed && renderIdentification()}
        {!method && identConfirmed && renderMethodSelection()}
        {method === 'questionnaire' && renderQuestionnaire()}
        {method === 'camera' && renderCamera()}
        {method === 'express' && (
          <div className="space-y-4 animate-fade-in-up">
            <ExpressDiagnosisPage
              embedded={true}
              initialIdentification={{
                mode: identMode,
                phone: clientPhone || null,
                first_name: clientName.trim().split(' ')[0] || 'Client',
                birth_year: clientBirthYear ? parseInt(clientBirthYear, 10) : null,
                client_code: generatedCode || clientCode || null,
              }}
              onComplete={(result) => {
                // Adapter le résultat Express → format produits standard
                const d = result.diagnosis || {};
                // concerns doit être un dict {clé: pourcentage} pour la barre de progression
                const concernsDict = {};
                if (d.concern) concernsDict[d.concern] = 75;
                if (d.allergy === 'oui') concernsDict.allergy = 90;
                const tips = [];
                if (d.advice) tips.push({ tip_fr: d.advice, tip_en: d.advice });
                if (d.ingredients_to_avoid?.length) {
                  tips.push({
                    tip_fr: `À éviter : ${d.ingredients_to_avoid.join(', ')}`,
                    tip_en: `Avoid: ${d.ingredients_to_avoid.join(', ')}`,
                  });
                }
                const adaptedResult = {
                  id: result.id,
                  client_id: result.client?.id || clientId,
                  client_name: result.client?.first_name || clientName,
                  client_phone: result.client?.phone || clientPhone,
                  client_code: result.client?.client_code || result.generated_client_code,
                  generated_client_code: result.generated_client_code,
                  skin_type: d.skin_type || 'normal',
                  main_concern: d.concern || 'general',
                  skin_score: 75,
                  concerns: concernsDict,
                  morning_routine: [],
                  evening_routine: [],
                  weekly_routine: [],
                  active_ingredients: [],
                  skin_tips: tips,
                  supplements: [],
                  recommendations: (d.products || []).map((p) => ({
                    product_id: p.id,
                    product_name: p.name,
                    brand: p.brand,
                    price: p.price,
                    image_url: p.image_url,
                    reason: d.advice,
                  })),
                  ingredients_to_avoid: d.ingredients_to_avoid || [],
                  analysis_summary: d.advice,
                  analysis_method: 'express',
                };
                toast.success(language === 'fr' ? 'Diagnostic Express terminé!' : 'Express diagnosis complete!');
                onComplete(adaptedResult);
              }}
              onBack={() => setMethod(null)}
            />
            <Button
              variant="outline"
              onClick={() => setMethod(null)}
              className="w-full"
              data-testid="express-cancel-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Changer de mode' : 'Change mode'}
            </Button>
          </div>
        )}
        {method === 'comprehensive' && (
          <ComprehensiveDiagnosis 
            language={language}
            initialIdentification={{
              mode: identMode,
              phone: clientPhone || null,
              client_code: generatedCode || clientCode || null,
              first_name: clientName.trim().split(' ')[0] || '',
              birth_year: clientBirthYear ? parseInt(clientBirthYear, 10) : null,
            }}
            onComplete={(result) => {
              // Adapter le résultat pour la page de produits
              const adaptedResult = {
                id: result.id,
                client_id: result.client_id,
                client_name: result.client_name,
                client_phone: result.client_phone,
                skin_type: result.skin_results?.zones?.[0]?.skin_type || 'normal',
                main_concern: result.skin_results?.zones?.[0]?.concerns?.[0] || 'general',
                skin_score: 75,
                concerns: result.skin_results || {},
                morning_routine: [],
                evening_routine: [],
                weekly_routine: [],
                active_ingredients: [],
                skin_tips: [],
                supplements: [],
                recommendations: result.recommendations,
                comprehensive_results: {
                  skin: result.skin_results,
                  hair: result.hair_results,
                  nails: result.nails_results
                },
                analysis_method: 'comprehensive'
              };
              onComplete(adaptedResult);
            }}
            onCancel={() => setMethod(null)}
          />
        )}
        {method === 'voice' && (
          <div className="space-y-6 animate-fade-in-up">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-[#0F172A]">
                {language === 'fr' ? 'Diagnostic Vocal' : 'Voice Diagnosis'}
              </h2>
              <p className="text-sm text-[#78716C] mt-1">
                {language === 'fr' 
                  ? 'Décrivez les symptômes cutanés du client à voix haute'
                  : 'Describe the client\'s skin symptoms out loud'}
              </p>
            </div>
            
            <VoiceRecorder
              mode="skincare"
              clientName={clientName || existingClient?.first_name}
              clientPhone={clientPhone}
              language={language}
              onAnalysisComplete={async (result) => {
                try {
                  // Create client if new
                  let finalClientId = clientId;
                  if (isAgent && !existingClient && clientPhone && clientName) {
                    finalClientId = await createNewClient();
                  }
                  
                  // Adapter le résultat vocal pour la page de produits
                  const adaptedResult = {
                    id: `voice_${Date.now()}`,
                    client_id: finalClientId || clientId,
                    client_name: clientName || existingClient?.first_name || 'Client',
                    client_phone: clientPhone,
                    skin_type: result.skin_type || 'normal',
                    main_concern: result.main_concerns?.[0] || 'general',
                    skin_score: result.severity === 'mild' ? 80 : result.severity === 'moderate' ? 60 : 40,
                    concerns: {
                      main_concerns: result.main_concerns || [],
                      specific_symptoms: result.diagnosis?.specific_symptoms || []
                    },
                    morning_routine: [],
                    evening_routine: [],
                    weekly_routine: [],
                    active_ingredients: [],
                    skin_tips: result.recommendations || [],
                    supplements: [],
                    recommendations: result.recommendations || [],
                    analysis_summary: result.diagnosis?.summary || '',
                    analysis_method: 'voice'
                  };
                  
                  toast.success(language === 'fr' ? 'Diagnostic vocal terminé!' : 'Voice diagnosis complete!');
                  onComplete(adaptedResult);
                } catch (error) {
                  console.error('Voice diagnosis error:', error);
                  toast.error(language === 'fr' ? 'Erreur lors du diagnostic' : 'Diagnosis error');
                }
              }}
            />
            
            <Button
              variant="outline"
              onClick={() => setMethod(null)}
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Retour' : 'Back'}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default SkinAnalysisPage;
